import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CalendarCheck, Link as LinkIcon, Star, EnvelopeSimple, ArrowsClockwise, PaperPlaneTilt, CaretLeft, CaretRight } from "@phosphor-icons/react";
import LeadDetail from "./LeadDetail";
import { STATUS_LABEL, ScorePill, fmtDate } from "./meta";

const COL_COLOR = { interesse: "#22C55E", converti: "#22C55E", non_interesse: "#8A8A93", a_relancer: "#F5A623" };

function Card({ it, onOpen, onMove }) {
  return (
    <div className="bg-surface-2/60 border border-border rounded-md p-2.5 text-xs hover:border-accent/40 transition-colors duration-200" data-testid={`pipeline-card-${it.siren}`}>
      <button onClick={() => onOpen(it.siren)} className="text-left w-full">
        <div className="flex items-center justify-between gap-2">
          <span className="text-fg font-medium truncate flex items-center gap-1">{it.priority && <Star size={11} weight="fill" className="text-amber-400 shrink-0" />}{it.nom_commercial || it.name}</span>
          <ScorePill score={it.score_effective} cls={it.classification_effective} />
        </div>
        <div className="text-[10px] text-muted mt-1 flex items-center gap-2 flex-wrap">
          <span>{it.region || "—"}</span>{it.owner_name && <span>· {it.owner_name}</span>}
          {it.has_contact_email && <span className="flex items-center gap-0.5 text-accent"><EnvelopeSimple size={10} /> email</span>}
          {it.next_action_at && <span className="flex items-center gap-0.5 text-amber-400"><CalendarCheck size={10} /> {fmtDate(it.next_action_at)}</span>}
        </div>
      </button>
      <select value={it.prospect_status} onChange={(e) => onMove(it.siren, e.target.value)} data-testid={`pipeline-move-${it.siren}`}
        className="mt-2 w-full bg-surface border border-border rounded text-[11px] px-1.5 py-1 text-muted">
        {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
    </div>
  );
}

function TaskList({ title, items, onOpen, testid, tone }) {
  if (!items.length) return null;
  return (
    <div data-testid={testid}>
      <div className={`text-[11px] uppercase tracking-wider mb-1 ${tone}`}>{title} ({items.length})</div>
      {items.map((i) => (
        <button key={i.siren} onClick={() => onOpen(i.siren)} className="w-full text-left text-xs border-b border-border/50 py-1.5 hover:text-accent">
          <span className="text-fg">{i.nom_commercial || i.name}</span> <span className="text-muted">· {fmtDate(i.next_action_at)}{i.next_action_note ? ` · ${i.next_action_note}` : ""}</span>
        </button>
      ))}
    </div>
  );
}

function Tasks({ t, onOpen, reload }) {
  const linkOrgs = async () => {
    try { const { data } = await api.post("/admin/leads/link-orgs"); toast.success(`${data.linked} prospect(s) rapproché(s), ${data.converted} converti(s)`); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const empty = !t.overdue.length && !t.today.length && !t.upcoming.length;
  return (
    <aside className="surface rounded-lg p-4 space-y-4" data-testid="lead-tasks">
      <div className="overline flex items-center gap-2"><CalendarCheck size={13} className="text-accent" /> Relances & prochaines actions</div>
      {empty && <p className="text-xs text-muted" data-testid="lead-tasks-empty">Aucune action planifiée. Planifiez-en depuis une fiche prospect.</p>}
      <TaskList title="En retard" items={t.overdue} onOpen={onOpen} testid="lead-tasks-overdue" tone="text-destructive" />
      <TaskList title="Aujourd'hui" items={t.today} onOpen={onOpen} testid="lead-tasks-today" tone="text-accent" />
      <TaskList title="À venir (14 j)" items={t.upcoming} onOpen={onOpen} testid="lead-tasks-upcoming" tone="text-muted" />
      {t.followups.length > 0 && (
        <div data-testid="lead-tasks-followups">
          <div className="text-[11px] uppercase tracking-wider mb-1 text-muted">Relances de campagne programmées</div>
          {t.followups.map((f) => (
            <div key={f.campaign_id} className="text-xs py-1 flex items-center gap-1.5"><PaperPlaneTilt size={11} className="text-accent" />{f.name} · {new Date(f.due_at).toLocaleDateString("fr-FR")} · {f.eligible} destinataire(s)</div>
          ))}
        </div>
      )}
      <button onClick={linkOrgs} data-testid="lead-link-orgs-btn" className="w-full text-xs border border-border rounded-md py-2 flex items-center justify-center gap-1.5 hover:border-accent/40">
        <LinkIcon size={12} /> Rapprocher les clients interKO
      </button>
      <p className="text-[10px] text-muted">Rapprochement par domaine email des comptes interKO (exécuté aussi chaque nuit) : un prospect abonné passe automatiquement en « Converti ».</p>
    </aside>
  );
}

export default function LeadPipeline() {
  const [cols, setCols] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [owners, setOwners] = useState([]);
  const [owner, setOwner] = useState("");
  const [open, setOpen] = useState(null);
  const rail = useRef(null);
  const [atEnd, setAtEnd] = useState(false);
  const scroll = (dir) => rail.current?.scrollBy({ left: dir * 480, behavior: "smooth" });
  const onRail = (e) => { const el = e.currentTarget; setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4); };
  const load = useCallback(() => {
    api.get("/admin/leads/pipeline", { params: owner ? { owner } : {} }).then(({ data }) => setCols(data.columns)).catch(() => {});
    api.get("/admin/leads/tasks").then(({ data }) => setTasks(data)).catch(() => {});
  }, [owner]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/leads/owners").then(({ data }) => setOwners(data.owners)).catch(() => {}); }, []);
  const move = async (siren, status) => {
    try { await api.patch(`/admin/leads/companies/${siren}/prospect`, { status }); toast.success(`Déplacé vers « ${STATUS_LABEL[status]} »`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!cols || !tasks) return <div className="text-muted text-sm animate-pulse">Chargement…</div>;
  return (
    <div className="grid xl:grid-cols-[1fr_300px] gap-4" data-testid="lead-pipeline">
      <div className="space-y-3 min-w-0">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted">Commercial</span>
          <select value={owner} onChange={(e) => setOwner(e.target.value)} className="bg-surface-2 border border-border rounded px-2 py-1" data-testid="pipeline-owner-filter">
            <option value="">Tous</option>{owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <button onClick={load} className="text-muted hover:text-fg flex items-center gap-1 ml-auto" data-testid="pipeline-refresh"><ArrowsClockwise size={12} /> Actualiser</button>
        </div>
        <div className="flex items-center justify-end gap-1 text-[11px] text-muted" data-testid="pipeline-scroll-hint">
          <span className="mr-1">{cols.length} colonnes · faites défiler</span>
          <button onClick={() => scroll(-1)} className="border border-border rounded p-1 hover:border-accent/40 hover:text-accent" data-testid="pipeline-scroll-left"><CaretLeft size={12} /></button>
          <button onClick={() => scroll(1)} className="border border-border rounded p-1 hover:border-accent/40 hover:text-accent" data-testid="pipeline-scroll-right"><CaretRight size={12} /></button>
        </div>
        <div className="relative">
        <div ref={rail} onScroll={onRail} className="flex gap-3 overflow-x-auto pb-2 scroll-smooth" data-testid="pipeline-rail">
          {cols.map((c) => (
            <div key={c.status} className="w-56 shrink-0 surface rounded-lg p-2.5" data-testid={`pipeline-col-${c.status}`}>
              <div className="flex items-center justify-between mb-2 px-0.5">
                <span className="text-xs font-semibold" style={{ color: COL_COLOR[c.status] || "#E4E4E7" }}>{c.label}</span>
                <span className="text-[11px] font-mono text-muted" data-testid={`pipeline-count-${c.status}`}>{c.total}</span>
              </div>
              <div className="space-y-2 max-h-[560px] overflow-y-auto pr-0.5">
                {c.items.map((it) => <Card key={it.siren} it={it} onOpen={setOpen} onMove={move} />)}
                {!c.items.length && <p className="text-[11px] text-muted/60 px-0.5">Vide</p>}
              </div>
            </div>
          ))}
        </div>
        {!atEnd && <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-bg to-transparent" data-testid="pipeline-fade" />}
        </div>
        <p className="text-[11px] text-muted">« Nouveau » et « Exclu » ne sont pas affichés : ajoutez un prospect au suivi commercial depuis l'onglet Prospects.</p>
      </div>
      <Tasks t={tasks} onOpen={setOpen} reload={load} />
      <LeadDetail siren={open} onClose={() => setOpen(null)} onChanged={load} owners={owners} />
    </div>
  );
}
